import {
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  Output,
  SimpleChanges, ViewChild, ViewContainerRef
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { Overlay } from "@angular/cdk/overlay";
import { CommonUtils } from "../../utils/common-utils";
import { NzOptionComponent, NzSelectComponent } from "ng-zorro-antd/select";
import { ICellEditorAngularComp } from "ag-grid-angular";
import { ICellEditorParams } from "ag-grid-community";
import { GridTableService } from "../../services/grid-table.service";

@Component({
  selector: "app-multi-select",
  standalone: true,
  templateUrl: "./multi-select.component.html",
  styleUrls: ["./multi-select.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSelectComponent,
    NzOptionComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true
    }
  ]
})
export class MultiSelectComponent implements ControlValueAccessor, ICellEditorAngularComp {
  @Input() options: any[] = [];
  @Input() valueKey!: string;
  @Input() displayKey!: string;
  @Input() isMultiSelect: boolean = true;
  @Input() placeholder: string = "Select option(s)";
  @Input() selectedItems: any[] = [];
  @Input() showChips: boolean = true;
  @Input() disable: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() returnRawValue: boolean = false;
  @Input() selectDefault: boolean = false;

  @Output() selectionChange = new EventEmitter<any>();
  @Output() onClearSelection = new EventEmitter<void>();
  @Output() selectedItemsChange = new EventEmitter<any[]>();

  @ViewChild("multiSelect", { static: false, read: ElementRef }) multiSelectEl!: ElementRef<HTMLElement>;
  @ViewChild("multiSelect") multiSelectComponent!: NzSelectComponent;

  filteredOptions: any[] = [];
  public ngControl: NgControl | null = null;
  private cachedValue: any = null;
  private params: any = null;

  onChange: (value: any) => void = () => {
  };
  onTouched: () => void = () => {
  };

  constructor(private cdr: ChangeDetectorRef,
              private injector: Injector,
              private overlay: Overlay,
              private gridTableService: GridTableService) {
  }

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    this.filteredOptions = [...(this.options ?? [])];

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["options"]) {
      this.filteredOptions = [...(this.options ?? [])];

      if (this.cachedValue != null) {
        this.applyValue(this.cachedValue);

        const stillInvalid = !this.selectedItems?.length;
        if (stillInvalid) {
          this.cachedValue = null;
          this.handleDefaultSelection();
        } else {
          this.cachedValue = null;
        }
        return;
      }

      // no cached value, just validate current selection
      if (this.selectedItems?.length) {
        this.syncSelectedItemsWithOptions();
      } else {
        this.handleDefaultSelection();
      }
    }
  }

  writeValue(value: any): void {
    this.cachedValue = value;
    if (!this.options || this.options.length === 0) {
      return;
    }
    this.applyValue(value);
  }

  private applyValue(value: any): boolean {
    if (!this.options?.length) {
      return false;
    }

    if (Array.isArray(value)) {
      this.selectedItems = this.mapArrayValue(value);
    } else if (CommonUtils.isNotNil(value)) {
      const selected = this.mapSingleValue(value);
      if (!selected || (Array.isArray(selected) && !selected.length)) {
        return false;
      }
      this.selectedItems = Array.isArray(selected) ? selected : [selected];
    } else {
      this.selectedItems = [];
    }

    this.propagateChange();
    return true;
  }

  private mapArrayValue(value: any[]): any[] {
    if (value.length && typeof value[0] === "string") {
      return value
        .map(val => this.options.find(opt => opt[this.valueKey] === val))
        .filter(Boolean);
    }
    return value;
  }

  private mapSingleValue(value: any): any[] {
    const selected =
      ["string", "number", "boolean"].includes(typeof value)
        ? this.options.find(opt => opt[this.valueKey] === value)
        : value;
    return selected ? [selected] : [];
  }

  private syncSelectedItemsWithOptions(): void {
    const validSelectedItems = this.selectedItems.filter(item =>
      this.options.some(opt => opt[this.valueKey] === item[this.valueKey])
    );

    if (validSelectedItems.length !== this.selectedItems.length) {
      this.selectedItems = validSelectedItems;
      this.propagateChange();
    }
  }

  private propagateChange(): void {
    if (!this.selectedItems?.length) return;

    const raw = this.returnRawValue
      ? this.selectedItems
      : this.selectedItems.map(i => i[this.valueKey]);

    const valueToEmit = this.isMultiSelect ? raw : raw[0];
    this.onChange(valueToEmit);
    if (!this.isMultiSelect) {
      this.selectionChange.emit(this.selectedItems[0]);
    }
    this.selectedItemsChange.emit(this.selectedItems);
  }

  compareFn = (o1: any, o2: any): boolean => {
    if (!o1 || !o2) return false;
    return o1[this.valueKey] === o2[this.valueKey];
  };

  toggleSelection(option: any): void {
    if (!option || option.length === 0) {
      this.selectedItems = [];
      this.onChange(this.isMultiSelect ? [] : null);
      this.onClearSelection.emit();
      return;
    }
    if (this.isMultiSelect) {
      this.selectedItems = option;
      this.onChange(this.returnRawValue ? this.selectedItems : this.selectedItems.map(item => item[this.valueKey]));
    } else {
      this.selectedItems = [option];
      this.selectionChange.emit(option);
      this.onChange(this.returnRawValue ? option : option[this.valueKey]);
    }

    this.validateNow();
    this.selectedItemsChange.emit(this.selectedItems);
    this.gridTableService.commitAndRefocusCell(this.params);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disable = isDisabled;
  }

  setOptions(options: any[]): void {
    this.options = options;
    this.filteredOptions = [...this.options];
  }

  isSelected(option: any): boolean {
    return this.selectedItems.some((item: any) => item[this.valueKey] === option[this.valueKey]);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  hasError() {
    return this.ngControl?.control?.invalid && this.ngControl.control?.touched && this.ngControl.control?.errors;
  }

  onClear() {

  }

  private handleDefaultSelection() {
    if (this.isMultiSelect) return; //do not select default for multi-select

    if (this.selectDefault && this.options && this.options.length > 0 && (!this.selectedItems || this.selectedItems.length === 0)) {
      this.selectedItems = [this.options[0]];
      this.propagateChange();
    }
  }

  validateNow() {
    this.params?.validate?.();
  }

  agInit(params: ICellEditorParams): void {
    this.params = params;
    Object.keys(params).forEach((k: string) => {
      if ((this as any).hasOwnProperty(k)) {
        (this as any)[k] = (params as any)[k];
      }
    });
    this.writeValue(params.value);
  }

  getValue(): any {
    if (this.returnRawValue) {
      return this.isMultiSelect
        ? this.selectedItems
        : (this.selectedItems[0] || null);
    } else {
      const raw = this.selectedItems.map(i => i[this.valueKey]);
      return this.isMultiSelect
        ? raw
        : (raw[0] ?? null);
    }
  }

  isCancelAfterEnd(): boolean {
    return false;
  }

  afterGuiAttached() {
    setTimeout(() => {
      this.multiSelectComponent.focus();
    }, 0);
  }

  getValidationElement(): HTMLElement {
    return this.multiSelectEl.nativeElement;
  }

  getValidationErrors(): string[] | null {
    return this.gridTableService.validateEditableGrid(this.params.col, this.selectedItems[0]);
  }

  getNestedValue(obj: any, path: string): any {
    return CommonUtils.getNestedValue(obj, path);
  }
}
